import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Grid, Autocomplete, CircularProgress, Alert, Chip, Box, Typography, Divider,
} from '@mui/material';
import { SportsScore } from '@mui/icons-material';
import axios from 'axios';
import { BASE_URL, apiCall } from '../api/apiClient';

// ── Shared design tokens ───────────────────────────────────────────────────
const BG        = '#f5f8f6';
const SURFACE   = '#ffffff';
const BORDER    = '#d0ddd7';
const ACCENT    = '#2e7d5e';
const ACCENT_BG = '#e8f5ee';
const TEXT_PRI  = '#0f1f14';
const TEXT_SEC  = '#4b6358';
// ──────────────────────────────────────────────────────────────────────────

const menuProps = {
  PaperProps: {
    sx: {
      bgcolor: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: '10px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      '& .MuiMenuItem-root': {
        color: TEXT_PRI,
        fontSize: 14,
        '&:hover': { bgcolor: ACCENT_BG },
        '&.Mui-selected': { bgcolor: ACCENT_BG, color: ACCENT, fontWeight: 600 },
        '&.Mui-selected:hover': { bgcolor: ACCENT_BG },
      },
    },
  },
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: SURFACE,
    color: TEXT_PRI,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: ACCENT },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
  },
  '& .MuiInputLabel-root': { color: TEXT_SEC, fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  '& .MuiSelect-icon': { color: TEXT_SEC },
};

const CATEGORIES = ['group_stage', 'round_of_16', 'quarter_final', 'semi_final', 'final', 'league', 'friendly'];

const GameDialog = ({ open, onClose, eventId, onCreated }) => {
  const [form, setForm] = useState({
    categorie: 'friendly', entity1: null, entity2: null,
    gameDate: '', startTime: '', venue: { name: '', city: '' },
  });
  const [entities, setEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !eventId) return;

    const fetchEntities = async () => {
      setLoadingEntities(true);
      setError('');
      try {
        const res = await apiCall((headers) =>
          axios.get(`${BASE_URL}/events/${eventId}`, { headers })
        );

        const event = res.data?.data ?? res.data;

        const fromEntitiesParticipants = (event.entitiesParticipants || [])
          .filter((e) => e && typeof e === 'object' && e._id);

        const fromParticipants = (event.participants || [])
          .map((p) => p?.entityId)
          .filter((e) => e && typeof e === 'object' && e._id);

        let resolved =
          fromEntitiesParticipants.length > 0
            ? fromEntitiesParticipants
            : fromParticipants;

        const seen = new Set();
        resolved = resolved.filter((e) => {
          if (seen.has(e._id)) return false;
          seen.add(e._id);
          return true;
        });

        if (resolved.length === 0) {
          setError('No entities found for this event. Make sure participants are confirmed and entities are populated.');
        }

        setEntities(resolved);
      } catch (e) {
        console.error('GameDialog fetchEntities error:', e);
        setError('Failed to load event entities');
      } finally {
        setLoadingEntities(false);
      }
    };

    fetchEntities();
  }, [open, eventId]);

  const handleSubmit = async () => {
    if (!form.entity1 || !form.entity2 || !form.gameDate) {
      setError('Team 1, Team 2 and Game Date are required');
      return;
    }
    if (form.entity1._id === form.entity2._id) {
      setError('Team 1 and Team 2 must be different');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await apiCall((headers) =>
        axios.post(`${BASE_URL}/games`, {
          eventId,
          categorie: form.categorie,
          entity1: form.entity1._id,
          entity2: form.entity2._id,
          gameDate: form.gameDate,
          startTime: form.startTime,
          venue: form.venue,
        }, { headers })
      );
      onCreated();
      onClose();
      setForm({
        categorie: 'friendly', entity1: null, entity2: null,
        gameDate: '', startTime: '', venue: { name: '', city: '' },
      });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create game');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) { setError(''); onClose(); }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          bgcolor: SURFACE,
          border: `1px solid ${BORDER}`,
          boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
        },
      }}
    >
      {/* ── Title ── */}
      <DialogTitle
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          pb: 1.5, pt: 2.5, px: 3,
          color: TEXT_PRI, fontWeight: 700,
        }}
      >
        <SportsScore sx={{ color: ACCENT }} />
        Ajouter un nouveau match
      </DialogTitle>

      <Divider sx={{ borderColor: BORDER }} />

      {/* ── Content ── */}
      <DialogContent sx={{ pt: 2.5, px: 3 }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2, borderRadius: '10px',
              bgcolor: '#fef2f2', color: '#991b1b',
              border: '1px solid #fecaca',
              '& .MuiAlert-icon': { color: '#dc2626' },
            }}
          >
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Catégorie */}
          <Grid item xs={12}>
            <TextField
              select fullWidth label="Catégorie"
              value={form.categorie}
              onChange={(e) => setForm((p) => ({ ...p, categorie: e.target.value }))}
              sx={inputSx}
              SelectProps={{ MenuProps: menuProps }}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Équipe 1 */}
          <Grid item xs={12}>
            <Autocomplete
              options={entities}
              loading={loadingEntities}
              getOptionLabel={(o) => o?.name || ''}
              getOptionDisabled={(o) => o._id === form.entity2?._id}
              value={form.entity1}
              onChange={(_, v) => setForm((p) => ({ ...p, entity1: v }))}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              noOptionsText={loadingEntities ? 'Chargement…' : 'Aucune équipe trouvée'}
              slotProps={{
                paper: {
                  sx: {
                    bgcolor: SURFACE,
                    border: `1px solid ${BORDER}`,
                    borderRadius: '10px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    '& .MuiAutocomplete-option': {
                      color: TEXT_PRI, fontSize: 14,
                      '&:hover': { bgcolor: ACCENT_BG },
                      '&[aria-selected="true"]': { bgcolor: ACCENT_BG, color: ACCENT, fontWeight: 600 },
                    },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Équipe 1"
                  sx={inputSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingEntities && <CircularProgress size={18} sx={{ color: ACCENT }} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Équipe 2 */}
          <Grid item xs={12}>
            <Autocomplete
              options={entities}
              loading={loadingEntities}
              getOptionLabel={(o) => o?.name || ''}
              getOptionDisabled={(o) => o._id === form.entity1?._id}
              value={form.entity2}
              onChange={(_, v) => setForm((p) => ({ ...p, entity2: v }))}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              noOptionsText={loadingEntities ? 'Chargement…' : 'Aucune équipe trouvée'}
              slotProps={{
                paper: {
                  sx: {
                    bgcolor: SURFACE,
                    border: `1px solid ${BORDER}`,
                    borderRadius: '10px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    '& .MuiAutocomplete-option': {
                      color: TEXT_PRI, fontSize: 14,
                      '&:hover': { bgcolor: ACCENT_BG },
                      '&[aria-selected="true"]': { bgcolor: ACCENT_BG, color: ACCENT, fontWeight: 600 },
                    },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Équipe 2"
                  sx={inputSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingEntities && <CircularProgress size={18} sx={{ color: ACCENT }} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Date + Heure */}
          <Grid item xs={6}>
            <TextField
              fullWidth label="Date du match" type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.gameDate}
              onChange={(e) => setForm((p) => ({ ...p, gameDate: e.target.value }))}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth label="Heure de début" type="time"
              InputLabelProps={{ shrink: true }}
              value={form.startTime}
              onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
              sx={inputSx}
            />
          </Grid>

          {/* Stade */}
          <Grid item xs={6}>
            <TextField
              fullWidth label="Nom du stade"
              value={form.venue.name}
              onChange={(e) => setForm((p) => ({ ...p, venue: { ...p.venue, name: e.target.value } }))}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth label="Ville du stade"
              value={form.venue.city}
              onChange={(e) => setForm((p) => ({ ...p, venue: { ...p.venue, city: e.target.value } }))}
              sx={inputSx}
            />
          </Grid>
        </Grid>

        {/* ── Match preview ── */}
        {form.entity1 && form.entity2 && (
          <Box
            sx={{
              mt: 2.5, p: 1.5,
              bgcolor: BG,
              border: `1px solid ${BORDER}`,
              borderRadius: '12px',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: TEXT_SEC, fontSize: 12, mb: 0.75 }}>
              Aperçu du match
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
              {/* Team 1 badge */}
              <Box
                sx={{
                  display: 'inline-flex', alignItems: 'center',
                  px: 1.25, py: 0.4,
                  borderRadius: '8px',
                  border: `1px solid ${ACCENT}55`,
                  bgcolor: ACCENT_BG,
                  color: ACCENT,
                  fontSize: 13, fontWeight: 600,
                }}
              >
                {form.entity1.name}
              </Box>

              <Typography
                variant="caption"
                sx={{ fontWeight: 800, color: TEXT_SEC, fontSize: 11, letterSpacing: '0.08em' }}
              >
                VS
              </Typography>

              {/* Team 2 badge */}
              <Box
                sx={{
                  display: 'inline-flex', alignItems: 'center',
                  px: 1.25, py: 0.4,
                  borderRadius: '8px',
                  border: `1px solid ${BORDER}`,
                  bgcolor: BG,
                  color: TEXT_PRI,
                  fontSize: 13, fontWeight: 600,
                }}
              >
                {form.entity2.name}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* ── Actions ── */}
      <Divider sx={{ borderColor: BORDER }} />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            color: TEXT_SEC,
            border: `1px solid ${BORDER}`,
            px: 2,
            '&:hover': { bgcolor: BG, borderColor: TEXT_SEC },
          }}
        >
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || loadingEntities}
          startIcon={
            loading
              ? <CircularProgress size={16} sx={{ color: '#fff' }} />
              : <SportsScore />
          }
          sx={{
            bgcolor: ACCENT,
            color: '#ffffff',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            boxShadow: `0 4px 14px ${ACCENT}55`,
            '&:hover': { bgcolor: '#235f47', boxShadow: `0 6px 18px ${ACCENT}66` },
            '&.Mui-disabled': { bgcolor: BORDER, color: TEXT_SEC, boxShadow: 'none' },
          }}
        >
          Créer le match
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GameDialog;